/** @jsx jsx */
import { useEffect, useState } from "react";
import BaseInput from "../Input";
import StepContainer from "../StepContainer";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import { Flex, Box } from "rebass";
import { State } from "xstate";

import { jsx, css } from "@emotion/core";
import { useMachine } from "@xstate/react";
import { getStep3 } from "./machine";
import Button from "../Button";
import { EquipmentType } from "../../types/equipmentTypes";
import ImageInput from "../ImageInput";
import Thumbnail from "../ImageInput/Thumbnail";
import EquipmentTypeSelect from "../EquipmentTypeSelect";
import Loading from "../Loading";

const errors = {
  equipmentType: {
    empty: "Equipment type is a required feld",
  },
  manufacturer: {
    empty: "Manufacturer is a required field",
  },
  model: {
    empty: "Model is a required field",
  },
  year: {
    empty: "Year is a required field",
  },
  serialNumber: {
    empty: "Serial Number is a required field",
  },
  vin: {
    empty: "VIN is a required field",
  },
  quantity: {
    empty: "Quantity is a required field",
  },
  hoursMilesType: {
    empty: "Hours / Miles type is a required field",
  },
  hoursMiles: {
    empty: "Hours / Miles is a required field",
  },
};

function getErrorMessage(
  item: string,
  current: State<
    {
      companyName: string;
      streetAddress: string;
      streetAddress2?: string;
      city: string;
      state: string;
      postalCode: number;
      title: string;
      companyPhoneNumber: number;
      quantity: number;
    },
    any
  >
): string | undefined {
  const keys = errors[item] ? Object.keys(errors[item]) : [];
  let message = undefined;

  keys.some((key) => {
    if (current.matches(`ready.${item}.error.${key}`)) {
      message = errors[item][key];
    }
  });
  return message;
}

const Input = ({ current, ...props }) => (
  <BaseInput
    {...props}
    helperText={getErrorMessage(props.name, current)}
    css={css`
      margin-bottom: 20px;
    `}
  />
);

function Step3({
  equipmentType,
  manufacturer,
  model,
  year,
  serialNumber,
  vin,
  identifier,
  quantity,
  hoursMilesType = "hours",
  hoursMiles,
  images = [],
  onSubmit,
  onBack,
}: {
  equipmentType: EquipmentType;
  manufacturer: string;
  model: string;
  year: number;
  serialNumber: string;
  vin: string;
  identifier: string;
  quantity: number;
  hoursMilesType: string;
  hoursMiles: number;
  images: any;
  onSubmit: (values: any) => void;
  onBack: (values: any) => void;
}) {
  const [current, send] = useMachine(
    getStep3({
      equipmentType,
      manufacturer,
      model,
      year,
      serialNumber,
      vin,
      identifier,
      quantity,
      hoursMilesType,
      hoursMiles,
      images,
    })
  );
  const [image, _setImage] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const finished = current.matches("finished");

  useEffect(() => {
    finished && onSubmit(current.context);
  }, [finished, current.context, onSubmit]);

  const handleChange: any = (type: string, property: string) => (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    send({
      type,
      [property]: e.currentTarget ? e.currentTarget.value : "",
    });
  };

  /**
   * Image Being Added!
   */
  const handleAddImage = (name, value) => {
    send({
      type: "ADD_IMAGES",
      payload: { name, value },
    } as any);
  };

  const handleEquipmentTypeChange = handleChange(
    "INPUT_EQUIPMENT_TYPE",
    "equipmentType"
  );

  const handleManufacturerChange = handleChange(
    "INPUT_MANUFACTURER",
    "manufacturer"
  );

  const handleModelChange = handleChange("INPUT_MODEL", "model");

  const handleYearChange = handleChange("INPUT_YEAR", "year");

  const handleQuantityChange = handleChange("INPUT_QUANTITY", "quantity");

  const handleSerialNumberChange = handleChange(
    "INPUT_SERIAL_NUMBER",
    "serialNumber"
  );

  const handleVinChange = handleChange("INPUT_VIN", "vin");

  const handleIdentifierChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    send("INPUT_IDENTIFIER", {
      identifier: event.target.value as string,
    });
  };

  const handleHoursMilesTypeChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    send("INPUT_HOURS_MILES_TYPE", {
      hoursMilesType: event.target.value as string,
    });
  };

  const handleHoursMilesChange = handleChange(
    "INPUT_HOURS_MILES",
    "hoursMiles"
  );

  let showSerial = current.matches("ready.identifier.noError.serial");

  let showVin = current.matches("ready.identifier.noError.vin");

  if (current.matches("ready.identifier.noError.both")) {
    showSerial = true;
    showVin = true;
  }

  return (
    <StepContainer title="Step 3 / 5: Equipment Information">
      <Box mb="3">
        <EquipmentTypeSelect
          value={current.context.equipmentType}
          onChange={(value) => {
            send("INPUT_EQUIPMENT_TYPE", { equipmentType: value });
          }}
        />
        {current.matches("ready.equipmentType.error") && (
          <FormHelperText error>
            {getErrorMessage("equipmentType", current as any)}
          </FormHelperText>
        )}
      </Box>
      <Input
        required
        error={current.matches("ready.manufacturer.error")}
        label="Manufacturer"
        name="manufacturer"
        value={manufacturer}
        current={current}
        onChange={handleManufacturerChange}
      />
      <Input
        required
        error={current.matches("ready.model.error")}
        label="Model"
        name="model"
        value={model}
        current={current}
        onChange={handleModelChange}
      />
      <Input
        required
        error={current.matches("ready.year.error")}
        label="Year"
        name="year"
        value={year}
        current={current}
        onChange={handleYearChange}
      />
      <Box sx={{ width: "100%" }}>
        <FormControl style={{ width: "100%" }}>
          <InputLabel id="identifierTypeLabel">Identifier Type</InputLabel>
          <Select
            labelId="hoursMiles-type-label"
            id="demo-simple-select"
            value={current.context.identifier as any}
            onChange={handleIdentifierChange}
          >
            <MenuItem value="serial">Serial Number</MenuItem>
            <MenuItem value="vin">VIN</MenuItem>
            <MenuItem value="both">Serial Number and VIN</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {showSerial && (
        <Input
          required
          error={current.matches("ready.serialNumber.error")}
          label="Equipment Serial Number"
          name="serialNumber"
          value={serialNumber}
          current={current}
          onChange={handleSerialNumberChange}
        />
      )}
      {showVin && (
        <Input
          required
          error={current.matches("ready.vin.error")}
          label="Equipment VIN"
          name="vin"
          value={vin}
          current={current}
          onChange={handleVinChange}
        />
      )}
      <Input
        required
        error={current.matches("ready.quantity.error")}
        label="Quantity"
        name="quantity"
        value={quantity}
        current={current}
        onChange={handleQuantityChange}
      />
      <Flex width="70%" justifyContent="space-between" mt="3">
        <Box sx={{ width: "100%" }} mr="3">
          <Input
            required
            error={current.matches("ready.hoursMiles.error")}
            label="Hours / Miles"
            name="hoursMiles"
            value={hoursMiles}
            current={current}
            onChange={handleHoursMilesChange}
            style={{ width: "100%" }}
          />
        </Box>
        <Box sx={{ width: "100%" }}>
          <FormControl style={{ width: "100%" }}>
            <InputLabel id="hoursMiles-type-label">
              Hours / Miles Type
            </InputLabel>
            <Select
              labelId="hoursMiles-type-label"
              id="demo-simple-select"
              value={current.context.hoursMilesType as any}
              onChange={handleHoursMilesTypeChange}
            >
              <MenuItem value="hours">Hours</MenuItem>
              <MenuItem value="miles">Miles</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Flex>
      <Flex width="100%" justifyContent="flex-start" mt="3" flexWrap="wrap">
        {current.matches("ready.images.hasImages") &&
          current.context.images.map((image) => (
            <div style={{ position: "relative" }} key={(image as any).name}>
              <Thumbnail
                key={(image as any).name}
                thumbnail={(image as any).value}
              />
              <Button
                onClick={() =>
                  send({ type: "REMOVE_IMAGE", name: (image as any).name })
                }
                style={{ position: "absolute", bottom: 5, right: 5, left: 5 }}
              >
                Remove
              </Button>
            </div>
          ))}
      </Flex>
      <Box mt="3">
        {optimizing && <Loading />}
        <ImageInput
          name="Image"
          labelText=""
          value={image}
          setField={handleAddImage}
          disabled={optimizing}
          onOptimizing={setOptimizing}
        />
      </Box>
      <Flex justifyContent="space-between" mt="4">
        <Button
          onClick={() => onBack(current.context)}
          width={200}
          disabled={optimizing}
        >
          Back
        </Button>

        <Button
          onClick={() => {
            send("SUBMIT");
          }}
          width={200}
          color="primary"
          disabled={optimizing}
        >
          Continue
        </Button>
      </Flex>
    </StepContainer>
  );
}

export default Step3;
